import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { referralAPI, clientAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ClientMedicaidSection from './clientForm/sections/ClientMedicaidSection';
import SearchableProviderTeamOfficeSelect from './SearchableProviderTeamOfficeSelect';
import DeleteIconButton from './DeleteIconButton';
import { formatDisplayDate } from '../utils/dateFormat';
import { formatProviderSiteOptionLabel } from '../utils/providerDisplayLabel';
import './ReferralInsuranceWorkspaceSection.css';

const POSTURE_OPTIONS = [
  { value: 'PRIVATE_INSURANCE_BILL', label: 'Private insurance (bill)' },
  { value: 'PRIVATE_INSURANCE_DECLINE', label: 'Private insurance (decline)' },
  { value: 'NO_PRIVATE_INSURANCE', label: 'No private insurance' },
];

function postureLabel(value) {
  const o = POSTURE_OPTIONS.find((x) => x.value === value);
  return o ? o.label : value || '';
}

const WAIVERS_HELP_TEXT =
  'Waivers are stored per coverage period (often one per discipline or office). Add or change waivers only on the open ' +
  '(current) period; earlier periods are kept for reference. Service coordinators usually own waiver details after intake.';

const EXEMPTION_HELP_TEXT =
  'At most one exemption per coverage period. It is often entered after intake when the service coordinator confirms ' +
  'eligibility; when insurance or coverage changes, your process may require a new benefit verification and a new exemption ' +
  'decision. Use start, end, and note to match your agency records.';

/** Shared body for the Coverage “ⓘ” help (save line varies by screen — see helpPersistMessage). */
const INSURANCE_WORKSPACE_HELP_BODY =
  'At new referral, many teams only record private insurance, company, and plan type. Waivers and exemption are usually added later ' +
  'by the service coordinator (here, on the full referral form, or from the client record → Insurance → Edit insurance). ' +
  'Company and plan type reflect the open coverage row (blank end date). Waivers and exemptions belong to a specific coverage ' +
  'period; earlier periods appear under “Previous coverage” for reference only—not editable there. ';

/** SVG info mark — Unicode ⓘ sits low in many fonts; this centers optically next to headings */
function InsuranceHeadingInfoIcon() {
  return (
    <svg
      className="referral-insurance-ws-hero-info-mark"
      viewBox="0 0 20 20"
      width="20"
      height="20"
      aria-hidden
      focusable="false"
    >
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.35" />
      <circle cx="10" cy="7" r="1.15" fill="currentColor" />
      <rect x="9" y="9.6" width="2" height="5.2" rx="1" fill="currentColor" />
    </svg>
  );
}

function waiverFromApi(w) {
  const officeValue =
    w.provider_payee_site_id != null
      ? String(w.provider_payee_site_id)
      : w.provider_site_id != null
        ? String(w.provider_site_id)
        : w.provider_id
          ? `provider:${w.provider_id}`
          : '';
  const displayLabel = formatProviderSiteOptionLabel({
    first_name: w.waiver_provider_first_name,
    last_name: w.waiver_provider_last_name,
    payee_name: w.waiver_payee_name,
    provider_type_code: w.provider_type_code,
    site_street_1: w.waiver_site_street_1,
    site_city: w.waiver_site_city,
    site_label: w.waiver_site_label,
  });
  return {
    ...w,
    clientKey: w.id,
    provider_office_value: officeValue,
    provider_office_display: displayLabel,
  };
}

function emptyExemptionForm() {
  return { start_date: '', end_date: '', note: '' };
}

function newWaiverRow(openCoverageId) {
  return {
    clientKey: `w-${Date.now()}`,
    coverage_id: openCoverageId || '',
    provider_id: '',
    provider_office_value: '',
    provider_office_display: '',
    start_date: '',
    end_date: '',
    note: '',
  };
}

/**
 * Referral-scoped insurance: versioned coverage, waivers (provider + office), optional exemption per coverage period.
 */
function ReferralInsuranceWorkspaceSection({
  referralId,
  /** When set, shows “Client Medicaid” above coverage and saves with the insurance workspace. */
  clientId,
  insuranceCompanies,
  insuranceTypes,
  readOnly,
  registerSaveHandler,
  /** (dirty: boolean) => void — false after load/save; true when workspace edits are pending */
  onWorkspaceDirtyChange,
  /** Appended to coverage help — e.g. referral form vs slim insurance page */
  helpPersistMessage = 'Save the referral to persist changes.',
  /** When true, show + next to Company to open Settings → Insurance Companies (requires settings.view + settings.manage_lookups). */
  canManageInsuranceCompaniesLookup = false,
  /** Path to return to after managing lookup (pathname + search); omit if unknown. */
  referralFormPathForInsuranceLookup = '',
}) {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInsuranceHelp, setShowInsuranceHelp] = useState(false);
  const [showWaiversHelp, setShowWaiversHelp] = useState(false);
  const [exemptionHelpCoverageId, setExemptionHelpCoverageId] = useState(null);
  const [coverages, setCoverages] = useState([]);
  const [waivers, setWaivers] = useState([]);
  /** One optional exemption form per coverage id (server: at most one row per coverage). */
  const [exemptionsByCoverage, setExemptionsByCoverage] = useState({});
  const [clientMedicaidRin, setClientMedicaidRin] = useState('');
  const [clientHasMedicaid, setClientHasMedicaid] = useState(false);
  const [clientMedicaidDirty, setClientMedicaidDirty] = useState(false);

  const onWorkspaceDirtyChangeRef = useRef(onWorkspaceDirtyChange);
  onWorkspaceDirtyChangeRef.current = onWorkspaceDirtyChange;

  const markWorkspaceDirty = useCallback(() => {
    if (readOnly) return;
    onWorkspaceDirtyChangeRef.current?.(true);
  }, [readOnly]);

  const load = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    if (!referralId) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const cid = clientId != null && String(clientId).trim() !== '' ? String(clientId).trim() : '';
      const [insRes, clientRes] = await Promise.all([
        referralAPI.getInsuranceWorkspace(referralId),
        cid ? clientAPI.getById(cid) : Promise.resolve(null),
      ]);
      const d = insRes.data?.data || {};
      setCoverages(d.coverages || []);
      setWaivers((d.waivers || []).map(waiverFromApi));
      const exMap = {};
      (d.exemptions || []).forEach((ex) => {
        if (ex.coverage_id) {
          exMap[String(ex.coverage_id)] = {
            start_date: ex.start_date ? String(ex.start_date).split('T')[0] : '',
            end_date: ex.end_date ? String(ex.end_date).split('T')[0] : '',
            note: ex.note || '',
          };
        }
      });
      setExemptionsByCoverage(exMap);
      if (clientRes?.data?.data) {
        const c = clientRes.data.data;
        setClientMedicaidRin(c.medicaid_rin || '');
        setClientHasMedicaid(c.has_medicaid === true);
      } else {
        setClientMedicaidRin('');
        setClientHasMedicaid(false);
      }
      setClientMedicaidDirty(false);
      onWorkspaceDirtyChangeRef.current?.(false);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || 'Failed to load insurance');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [referralId, clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const { openCoverage, pastCoverages } = useMemo(() => {
    const openRows = (coverages || []).filter((c) => !c.effective_end);
    const open = openRows.length ? openRows[openRows.length - 1] : null;
    const past = (coverages || [])
      .filter((c) => !!c.effective_end)
      .slice()
      .sort((a, b) => {
        const as = a.effective_start ? String(a.effective_start).split('T')[0] : '';
        const bs = b.effective_start ? String(b.effective_start).split('T')[0] : '';
        return bs.localeCompare(as);
      });
    return { openCoverage: open, pastCoverages: past };
  }, [coverages]);

  const openCoverageId = openCoverage?.id || '';

  const handleClientMedicaidFieldChange = useCallback(
    ({ name, value }) => {
      if (readOnly) return;
      if (!hasPermission('clients.update')) return;
      setClientMedicaidDirty(true);
      markWorkspaceDirty();
      if (name === 'has_medicaid') setClientHasMedicaid(value);
      if (name === 'medicaid_rin') setClientMedicaidRin(value);
    },
    [readOnly, markWorkspaceDirty]
  );

  const clientMedicaidPanel = (() => {
    const cid = clientId != null && String(clientId).trim() !== '' ? String(clientId).trim() : '';
    if (!cid) return null;
    const canEditClientMedicaid = hasPermission('clients.update');
    const locked = readOnly || !canEditClientMedicaid;
    return (
      <ClientMedicaidSection
        className="client-medicaid-section--referral-insurance-embedded"
        hasMedicaid={clientHasMedicaid}
        medicaidRin={clientMedicaidRin}
        onMedicaidFieldChange={handleClientMedicaidFieldChange}
        disabled={locked}
        permissionHint={
          !readOnly && !canEditClientMedicaid
            ? 'You need permission to update clients to edit Medicaid on the client record.'
            : null
        }
      />
    );
  })();

  const updateCoverage = (id, patch) => {
    markWorkspaceDirty();
    setCoverages((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const addCoveragePeriod = () => {
    if (!openCoverage) return;
    const endCurrent = window.prompt(
      'End date for the current coverage period (YYYY-MM-DD). The new period will start the next calendar day.',
      new Date().toISOString().split('T')[0]
    );
    if (!endCurrent || !/^\d{4}-\d{2}-\d{2}$/.test(endCurrent)) return;
    const nextStart = new Date(`${endCurrent}T12:00:00`);
    nextStart.setDate(nextStart.getDate() + 1);
    const nextStartStr = nextStart.toISOString().split('T')[0];
    const newId = crypto.randomUUID();
    markWorkspaceDirty();
    setCoverages((prev) => {
      const closed = prev.map((c) => (!c.effective_end ? { ...c, effective_end: endCurrent } : c));
      return [
        ...closed,
        {
          id: newId,
          insurance_posture: openCoverage.insurance_posture || 'NO_PRIVATE_INSURANCE',
          insurance_company_id: openCoverage.insurance_company_id,
          insurance_type_id: openCoverage.insurance_type_id,
          effective_start: nextStartStr,
          effective_end: null,
          note: '',
        },
      ];
    });
  };

  const addWaiver = () => {
    if (!openCoverageId) return;
    markWorkspaceDirty();
    setWaivers((prev) => [...prev, newWaiverRow(openCoverageId)]);
  };

  const updateWaiver = (key, patch) => {
    markWorkspaceDirty();
    setWaivers((prev) => prev.map((w) => (w.clientKey === key || w.id === key ? { ...w, ...patch } : w)));
  };

  const handleWaiverOfficeChange = (key, { value, label, providerId, cleared }) => {
    updateWaiver(key, {
      provider_office_value: cleared ? '' : value != null ? String(value) : '',
      provider_office_display: cleared ? '' : (label || '').trim(),
      provider_id: cleared ? '' : providerId != null && String(providerId).trim() !== '' ? String(providerId) : '',
    });
  };

  const removeWaiver = (key) => {
    markWorkspaceDirty();
    setWaivers((prev) => prev.filter((w) => w.clientKey !== key && w.id !== key));
  };

  const setExemptionForCoverage = (coverageId, valueOrNull) => {
    markWorkspaceDirty();
    const key = String(coverageId);
    setExemptionsByCoverage((prev) => {
      const next = { ...prev };
      if (valueOrNull === null) {
        delete next[key];
      } else {
        next[key] = valueOrNull;
      }
      return next;
    });
  };

  const patchExemptionForCoverage = (coverageId, patch) => {
    markWorkspaceDirty();
    const key = String(coverageId);
    setExemptionsByCoverage((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || emptyExemptionForm()), ...patch },
    }));
  };

  const stateRef = useRef({ coverages, waivers, exemptionsByCoverage });
  stateRef.current = { coverages, waivers, exemptionsByCoverage };

  const clientMedicaidRef = useRef({
    clientId: '',
    rin: '',
    has: false,
    dirty: false,
  });
  clientMedicaidRef.current = {
    clientId: clientId != null && String(clientId).trim() !== '' ? String(clientId).trim() : '',
    rin: clientMedicaidRin,
    has: clientHasMedicaid,
    dirty: clientMedicaidDirty,
  };

  useEffect(() => {
    if (typeof registerSaveHandler !== 'function') return undefined;
    registerSaveHandler(async () => {
      if (readOnly || !referralId) return;
      const cm = clientMedicaidRef.current;
      if (cm.dirty && cm.clientId && hasPermission('clients.update')) {
        const digits = String(cm.rin || '').replace(/\D/g, '');
        if (digits.length > 0 && digits.length !== 9) {
          throw new Error('Client Medicaid RIN must be exactly 9 digits or left blank.');
        }
      }
      const { coverages: cov, waivers: wv, exemptionsByCoverage: exMap } = stateRef.current;
      const openRows = cov.filter((c) => !c.effective_end);
      if (openRows.length !== 1) {
        throw new Error('Exactly one coverage row must have a blank end date.');
      }
      for (const c of cov) {
        if (c.insurance_posture === 'PRIVATE_INSURANCE_BILL') {
          if (!c.insurance_company_id || !c.insurance_type_id) {
            throw new Error('Insurance company and plan type are required when billing private insurance.');
          }
        }
      }
      const covPayload = cov.map((c) => ({
        id: c.id,
        insurance_posture: c.insurance_posture,
        insurance_company_id:
          c.insurance_company_id != null && c.insurance_company_id !== '' ? parseInt(c.insurance_company_id, 10) : null,
        insurance_type_id:
          c.insurance_type_id != null && c.insurance_type_id !== '' ? parseInt(c.insurance_type_id, 10) : null,
        effective_start: c.effective_start ? String(c.effective_start).split('T')[0] : null,
        effective_end: c.effective_end ? String(c.effective_end).split('T')[0] : null,
        note: c.note || null,
      }));
      const wPayload = wv.map((w) => ({
        coverage_id: w.coverage_id,
        provider_id: w.provider_id,
        provider_office_value: w.provider_office_value != null ? String(w.provider_office_value).trim() : '',
        start_date: w.start_date ? String(w.start_date).split('T')[0] : null,
        end_date: w.end_date ? String(w.end_date).split('T')[0] : null,
        note: w.note || null,
      }));
      const exemptionsPayload = Object.entries(exMap || {})
        .filter(([, v]) => v && String(v.start_date || '').trim())
        .map(([coverage_id, v]) => ({
          coverage_id,
          start_date: String(v.start_date).split('T')[0],
          end_date: v.end_date ? String(v.end_date).split('T')[0] : null,
          note: v.note != null && String(v.note).trim() !== '' ? String(v.note) : null,
        }));
      await referralAPI.putInsuranceWorkspace(referralId, {
        coverages: covPayload,
        waivers: wPayload,
        exemptions: exemptionsPayload,
      });
      if (cm.dirty && cm.clientId && hasPermission('clients.update')) {
        const digits = String(cm.rin || '').replace(/\D/g, '');
        await clientAPI.update(cm.clientId, {
          has_medicaid: !!cm.has,
          medicaid_rin: digits.length === 9 ? digits : null,
        });
      }
      await load({ silent: true });
    });
    return () => registerSaveHandler(null);
  }, [registerSaveHandler, readOnly, referralId, load]);

  const currentWaivers = useMemo(() => {
    if (!openCoverageId) return waivers;
    return waivers.filter((w) => {
      const cid = w.coverage_id != null && String(w.coverage_id).trim() !== '' ? String(w.coverage_id) : '';
      return !cid || cid === String(openCoverageId);
    });
  }, [waivers, openCoverageId]);

  const orphanWaivers = useMemo(() => {
    const ids = new Set((coverages || []).map((c) => String(c.id)));
    return waivers.filter((w) => {
      const cid = w.coverage_id != null && String(w.coverage_id).trim() !== '' ? String(w.coverage_id) : '';
      if (!cid) return false;
      return !ids.has(cid);
    });
  }, [waivers, coverages]);

  const renderCoverageCard = (c, { historical = false } = {}) => {
    const locked = readOnly || historical;
    return (
      <div className={`referral-insurance-coverage-card${historical ? ' referral-insurance-coverage-card--historical' : ''}`}>
        <div className="referral-insurance-coverage-row1">
          <label className="referral-insurance-field referral-insurance-field--posture">
            <span className="referral-insurance-label">Private insurance</span>
            <select
              value={c.insurance_posture || ''}
              onChange={(e) => updateCoverage(c.id, { insurance_posture: e.target.value })}
              disabled={locked}
            >
              {POSTURE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <div className="referral-insurance-field referral-insurance-field--company">
            <div className="referral-insurance-company-header">
              <span className="referral-insurance-label">Company</span>
              {canManageInsuranceCompaniesLookup && !locked && (
                <button
                  type="button"
                  className="referral-insurance-btn-add-lookup"
                  onClick={() =>
                    navigate(tenantPath('/settings/insurance-companies'), {
                      state: referralFormPathForInsuranceLookup
                        ? { returnToPath: referralFormPathForInsuranceLookup }
                        : undefined,
                    })
                  }
                  title="Open Insurance Companies in Settings to add or edit companies, then return here"
                  aria-label="Open insurance companies lookup"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              )}
            </div>
            <select
              value={c.insurance_company_id != null ? String(c.insurance_company_id) : ''}
              onChange={(e) => updateCoverage(c.id, { insurance_company_id: e.target.value })}
              disabled={locked || c.insurance_posture !== 'PRIVATE_INSURANCE_BILL'}
            >
              <option value="">—</option>
              {insuranceCompanies.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <label className="referral-insurance-field referral-insurance-field--plantype">
            <span className="referral-insurance-label">Plan Type</span>
            <select
              value={c.insurance_type_id != null ? String(c.insurance_type_id) : ''}
              onChange={(e) => updateCoverage(c.id, { insurance_type_id: e.target.value })}
              disabled={locked || c.insurance_posture !== 'PRIVATE_INSURANCE_BILL'}
            >
              <option value="">—</option>
              {insuranceTypes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.description}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="referral-insurance-coverage-row2">
          <label className="referral-insurance-field referral-insurance-field--date">
            <span className="referral-insurance-label">Start</span>
            <input
              type="date"
              value={c.effective_start ? String(c.effective_start).split('T')[0] : ''}
              onChange={(e) => updateCoverage(c.id, { effective_start: e.target.value })}
              disabled={locked}
            />
          </label>
          <label className="referral-insurance-field referral-insurance-field--date">
            <span className="referral-insurance-label">End</span>
            <input
              type="date"
              value={c.effective_end ? String(c.effective_end).split('T')[0] : ''}
              onChange={(e) => updateCoverage(c.id, { effective_end: e.target.value || null })}
              disabled={locked}
            />
          </label>
        </div>
        <label className="referral-insurance-field referral-insurance-field--noteblock">
          <span className="referral-insurance-label">Note</span>
          <textarea
            className="referral-insurance-coverage-notearea"
            rows={2}
            value={c.note || ''}
            onChange={(e) => updateCoverage(c.id, { note: e.target.value })}
            disabled={locked}
            placeholder="Optional note for this period"
          />
        </label>
      </div>
    );
  };

  const renderWaiverList = (list, showCoveragePicker, historical = false) => {
    const rowLocked = readOnly || historical;
    return (
      <ul className="referral-insurance-ws-waiver-list">
        {list.map((w) => {
          const key = w.clientKey || w.id;
          return (
            <li
              key={key}
              className={`referral-insurance-ws-waiver-item${historical ? ' referral-insurance-ws-waiver-item--historical' : ''}`}
            >
              <div
                className={`referral-insurance-ws-waiver-top ${
                  !showCoveragePicker ? 'referral-insurance-ws-waiver-top--no-coverage-pick' : ''
                }`}
              >
                {showCoveragePicker ? (
                  <label className="referral-insurance-field referral-insurance-field--coveragepick">
                    <span className="referral-insurance-label">Coverage Row</span>
                    <select
                      value={w.coverage_id || ''}
                      onChange={(e) => updateWaiver(key, { coverage_id: e.target.value })}
                      disabled={rowLocked}
                    >
                      <option value="">Select…</option>
                      {coverages.map((c) => (
                        <option key={c.id} value={c.id}>
                          {formatDisplayDate(c.effective_start)}
                          {' — '}
                          {c.effective_end ? formatDisplayDate(c.effective_end) : 'open'}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <div className="referral-insurance-field referral-insurance-field--office">
                  <span className="referral-insurance-label" id={`waiver-office-lbl-${key}`}>
                    Provider &amp; Office
                  </span>
                  <div
                    className="referral-insurance-ws-office-wrap"
                    role="group"
                    aria-labelledby={`waiver-office-lbl-${key}`}
                  >
                    <SearchableProviderTeamOfficeSelect
                      id={`waiver-office-${key}`}
                      hiddenInputName={`referral_insurance_waiver_office_${key}`}
                      value={w.provider_office_value || ''}
                      displayLabel={w.provider_office_display || ''}
                      onChange={(payload) => handleWaiverOfficeChange(key, payload)}
                      disabled={rowLocked}
                      formatOptionLabel={formatProviderSiteOptionLabel}
                      placeholder="Search by name, type (e.g. ST), or payee…"
                      searchPlaceholder="Last name, first name, or type code…"
                      resultHint="Pick a specific office — same search as elsewhere on this form."
                    />
                  </div>
                </div>
                <label className="referral-insurance-field referral-insurance-field--wdate">
                  <span className="referral-insurance-label">Start</span>
                  <input
                    type="date"
                    value={w.start_date ? String(w.start_date).split('T')[0] : ''}
                    onChange={(e) => updateWaiver(key, { start_date: e.target.value })}
                    disabled={rowLocked}
                  />
                </label>
                <label className="referral-insurance-field referral-insurance-field--wdate">
                  <span className="referral-insurance-label">End</span>
                  <input
                    type="date"
                    value={w.end_date ? String(w.end_date).split('T')[0] : ''}
                    onChange={(e) => updateWaiver(key, { end_date: e.target.value || '' })}
                    disabled={rowLocked}
                  />
                </label>
              </div>
              <div className="referral-insurance-ws-waiver-note-row">
                <label className="referral-insurance-field referral-insurance-field--waivernote">
                  <span className="referral-insurance-label">Note</span>
                  <input
                    type="text"
                    value={w.note || ''}
                    onChange={(e) => updateWaiver(key, { note: e.target.value })}
                    disabled={rowLocked}
                  />
                </label>
                {!rowLocked && (
                  <DeleteIconButton
                    className="list-btn-delete"
                    onClick={() => removeWaiver(key)}
                    title="Delete waiver"
                    aria-label="Delete waiver"
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderExemptionForCoverage = (coverageId, { historical = false } = {}) => {
    const cid = String(coverageId);
    const form = exemptionsByCoverage[cid];
    const locked = readOnly || historical;
    const helpOpen = exemptionHelpCoverageId === cid;
    const helpId = `referral-insurance-ex-help-${cid.replace(/[^a-z0-9-]/gi, '')}`;

    return (
      <div className="referral-insurance-ws-exemption-sub">
        <div className="referral-insurance-ws-subsection-head referral-insurance-ws-subsection-head--exemption">
          <div className="referral-insurance-ws-head-title-inline">
            <h4 className="referral-insurance-ws-sub--minor referral-insurance-ws-sub--flush referral-insurance-ws-sub--minor-spaced">
              Exemption
            </h4>
            <button
              type="button"
              className="referral-form-info-icon referral-insurance-ws-inline-info"
              onClick={() => setExemptionHelpCoverageId(helpOpen ? null : cid)}
              aria-expanded={helpOpen}
              aria-controls={helpId}
              aria-label={helpOpen ? 'Hide exemption help' : 'Show exemption help'}
            >
              ⓘ
            </button>
          </div>
          {!locked && form == null && (
            <button
              type="button"
              className="btn-primary referral-insurance-ws-action-btn"
              onClick={() => setExemptionForCoverage(coverageId, emptyExemptionForm())}
            >
              + Exemption
            </button>
          )}
        </div>
        {helpOpen ? (
          <p id={helpId} className="referral-form-info-text referral-insurance-ws-inline-help">
            {EXEMPTION_HELP_TEXT}
            {historical ? ' This period is closed; exemption is shown for reference only.' : ''}
          </p>
        ) : null}
        {form ? (
          <div className="referral-insurance-ws-exemption">
            <label>
              Start
              <input
                type="date"
                value={form.start_date || ''}
                onChange={(e) => patchExemptionForCoverage(coverageId, { start_date: e.target.value })}
                disabled={locked}
              />
            </label>
            <label>
              End
              <input
                type="date"
                value={form.end_date || ''}
                onChange={(e) => patchExemptionForCoverage(coverageId, { end_date: e.target.value })}
                disabled={locked}
              />
            </label>
            <div className="referral-insurance-ws-exemption-note-with-delete">
              <label className="referral-insurance-ws-exemption-note">
                Note
                <input
                  type="text"
                  value={form.note || ''}
                  onChange={(e) => patchExemptionForCoverage(coverageId, { note: e.target.value })}
                  disabled={locked}
                />
              </label>
              {!locked && (
                <DeleteIconButton
                  className="list-btn-delete"
                  onClick={() => setExemptionForCoverage(coverageId, null)}
                  title="Delete exemption"
                  aria-label="Delete exemption"
                />
              )}
            </div>
          </div>
        ) : locked ? (
          <p className="referral-insurance-ws-muted referral-insurance-ws-muted--tight">None.</p>
        ) : null}
      </div>
    );
  };

  if (!referralId) return null;
  if (loading) return <div className="referral-insurance-ws loading">Loading insurance…</div>;
  if (error) {
    return (
      <div className="referral-insurance-ws error" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="referral-insurance-ws referral-insurance-ws--embedded referral-insurance-ws-shell">
      {clientMedicaidPanel}
      {openCoverage ? (
        <>
          <div className="referral-insurance-ws-subsection referral-insurance-ws-subsection--current-insurance">
            <div className="referral-insurance-ws-subsection-head referral-insurance-ws-subsection-head--current-insurance">
              <div className="referral-insurance-ws-head-title-inline">
                <h3 className="referral-insurance-ws-sub referral-insurance-ws-sub--flush">Current insurance</h3>
                <button
                  type="button"
                  className="referral-insurance-ws-hero-info-svg-btn"
                  onClick={() => setShowInsuranceHelp((v) => !v)}
                  aria-expanded={showInsuranceHelp}
                  aria-controls="referral-insurance-ws-help"
                  aria-label={showInsuranceHelp ? 'Hide insurance help' : 'Show insurance help'}
                >
                  <InsuranceHeadingInfoIcon />
                </button>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  className="btn-primary referral-insurance-ws-action-btn"
                  onClick={addCoveragePeriod}
                >
                  + New
                </button>
              )}
            </div>
            {showInsuranceHelp ? (
              <p id="referral-insurance-ws-help" className="referral-form-info-text referral-insurance-ws-inline-help">
                {INSURANCE_WORKSPACE_HELP_BODY}
                {helpPersistMessage}
              </p>
            ) : null}
            <div className="referral-insurance-coverage-list">
              <div key={openCoverage.id}>{renderCoverageCard(openCoverage)}</div>
            </div>
          </div>

          <div className="referral-insurance-ws-subsection">
            <div className="referral-insurance-ws-subsection-head">
              <div className="referral-insurance-ws-head-title-inline">
                <h4 className="referral-insurance-ws-sub--minor referral-insurance-ws-sub--flush">
                  Waivers ({currentWaivers.length})
                </h4>
                <button
                  type="button"
                  className="referral-form-info-icon referral-insurance-ws-inline-info"
                  onClick={() => setShowWaiversHelp((v) => !v)}
                  aria-expanded={showWaiversHelp}
                  aria-controls="referral-insurance-waivers-help"
                  aria-label={showWaiversHelp ? 'Hide waivers help' : 'Show waivers help'}
                >
                  ⓘ
                </button>
              </div>
              {!readOnly && (
                <button type="button" className="btn-primary referral-insurance-ws-action-btn" onClick={addWaiver}>
                  + Waiver
                </button>
              )}
            </div>
            {showWaiversHelp ? (
              <p id="referral-insurance-waivers-help" className="referral-form-info-text referral-insurance-ws-inline-help">
                {WAIVERS_HELP_TEXT}
              </p>
            ) : null}
            {currentWaivers.length ? renderWaiverList(currentWaivers, false, false) : null}
          </div>

          {openCoverage.id ? (
            <div className="referral-insurance-ws-subsection">{renderExemptionForCoverage(openCoverage.id)}</div>
          ) : null}

          {pastCoverages.length > 0 ? (
            <div className="referral-insurance-past-wrap">
              <h4 className="referral-insurance-past-heading">
                Previous coverage ({pastCoverages.length})
              </h4>
              {pastCoverages.map((c) => {
                const periodWaivers = waivers.filter((w) => String(w.coverage_id || '') === String(c.id));
                const sum = `${formatDisplayDate(c.effective_start)} — ${formatDisplayDate(c.effective_end)} · ${postureLabel(c.insurance_posture)}`;
                return (
                  <div key={c.id} className="referral-insurance-past-period">
                    <details className="referral-insurance-past-item">
                      <summary>{sum}</summary>
                      {renderCoverageCard(c, { historical: true })}
                      <h4 className="referral-insurance-ws-sub--minor referral-insurance-ws-sub--flush referral-insurance-ws-sub--minor-spaced">
                        Waivers ({periodWaivers.length})
                      </h4>
                      {periodWaivers.length ? renderWaiverList(periodWaivers, false, true) : null}
                      {renderExemptionForCoverage(c.id, { historical: true })}
                    </details>
                  </div>
                );
              })}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="referral-insurance-ws-subsection referral-insurance-ws-subsection--current-insurance">
            <div className="referral-insurance-ws-subsection-head referral-insurance-ws-subsection-head--current-insurance">
              <div className="referral-insurance-ws-head-title-inline">
                <h3 className="referral-insurance-ws-sub referral-insurance-ws-sub--flush">Coverage</h3>
                <button
                  type="button"
                  className="referral-insurance-ws-hero-info-svg-btn"
                  onClick={() => setShowInsuranceHelp((v) => !v)}
                  aria-expanded={showInsuranceHelp}
                  aria-controls="referral-insurance-ws-help"
                  aria-label={showInsuranceHelp ? 'Hide insurance help' : 'Show insurance help'}
                >
                  <InsuranceHeadingInfoIcon />
                </button>
              </div>
            </div>
            {showInsuranceHelp ? (
              <p id="referral-insurance-ws-help" className="referral-form-info-text referral-insurance-ws-inline-help">
                {INSURANCE_WORKSPACE_HELP_BODY}
                {helpPersistMessage}
              </p>
            ) : null}
            <p className="referral-insurance-ws-muted referral-insurance-ws-muted--tight">
              There must be exactly one coverage row with a blank end date. Add dates below or fix rows, then save.
            </p>
            <div className="referral-insurance-coverage-list">
              {(coverages || []).map((c) => (
                <div key={c.id} className="referral-insurance-repair-coverage-block">
                  {renderCoverageCard(c, { historical: !!c.effective_end })}
                  {renderExemptionForCoverage(c.id, { historical: !!c.effective_end })}
                </div>
              ))}
            </div>
          </div>
          <div className="referral-insurance-ws-subsection">
            <div className="referral-insurance-ws-subsection-head">
              <h3 className="referral-insurance-ws-sub referral-insurance-ws-sub--flush">Waivers</h3>
              {!readOnly && (coverages || []).length > 0 && (
                <button
                  type="button"
                  className="btn-primary referral-insurance-ws-action-btn"
                  onClick={() => {
                    const firstId = (coverages || [])[0]?.id;
                    if (!firstId) return;
                    markWorkspaceDirty();
                    setWaivers((prev) => [...prev, newWaiverRow(String(firstId))]);
                  }}
                >
                  + Waiver
                </button>
              )}
            </div>
            {waivers.length ? renderWaiverList(waivers, true) : (
              <p className="referral-insurance-ws-muted referral-insurance-ws-muted--tight">No waivers.</p>
            )}
          </div>
        </>
      )}

      {orphanWaivers.length > 0 ? (
        <div className="referral-insurance-ws-subsection referral-insurance-ws-subsection--last">
          <h3 className="referral-insurance-ws-sub referral-insurance-ws-sub--flush">Waivers (assign to a period)</h3>
          <p className="referral-insurance-ws-muted referral-insurance-ws-muted--tight">
            These waivers point at a coverage row that is no longer on this referral. Pick a coverage row or remove them.
          </p>
          {renderWaiverList(orphanWaivers, true)}
        </div>
      ) : null}
    </div>
  );
}

export default ReferralInsuranceWorkspaceSection;
